import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import WeatherWidget from '../components/WeatherWidget';
import type { DocumentoPersonale } from '../services/api';
import { DashboardStats, PuntoVendita } from '../types';
import {
  BarChart3,
  IceCream,
  TrendingUp,
  Users,
  Calendar,
  Archive,
  Trash2,
  Banknote,
  CreditCard,
  Coins,
  PlusCircle,
  FileText,
  ChevronRight,
  Cloud,
  Clock,
  AlertTriangle,
  MapPin,
  Factory,
  Wallet,
  Landmark,
  ClipboardList,
  RefreshCw,
} from 'lucide-react';
import {
  PageContainerWide,
  Select,
  Button,
  Card,
  Alert,
  Badge,
  LoadingState,
} from '../components/ui';

export {};

type Ruolo = 'admin' | 'manager' | 'produzione' | 'banconista';

interface ExtendedDashboardStats extends DashboardStats {
  totale_produzioni?: number;
  punti_vendita?: number;
  utenti_attivi?: number;
  costo_totale?: number;
  produzioni_ultimi_7_giorni?: number;
}

interface IncassoPeriodo {
  totale: number;
  contanti: number;
  elettronico: number;
  altro: number;
}

interface IncassoSummary {
  data_riferimento: string;
  ultimo_aggiornamento: string | null;
  da_preparare: { totale: number; settimane: number } | null;
  oggi: IncassoPeriodo;
  ultimi7: IncassoPeriodo;
  mese: IncassoPeriodo;
  anno: IncassoPeriodo;
}

interface DashboardPageProps {
  onNavigate?: (page: string) => void;
}

/* ---------- layout ---------- */

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
  margin-bottom: var(--space-xl);
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
`;

const HeaderSubtitle = styled.p`
  margin: var(--space-xs) 0 0;
  color: var(--text-secondary);
  font-size: var(--font-size-base);
`;

const HeaderAside = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-sm);
`;

const ServerClock = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  color: var(--text-muted);
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const ViewPicker = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);

  label {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    white-space: nowrap;
  }
`;

const SectionHeader = styled.h2`
  display: flex;
  align-items: baseline;
  gap: var(--space-sm);
  margin: var(--space-xl) 0 var(--space-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--gray-600);

  svg {
    width: 1rem;
    height: 1rem;
    color: var(--gray-500);
    align-self: center;
  }

  span.meta {
    margin-left: auto;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-normal);
    text-transform: none;
    letter-spacing: normal;
    color: var(--text-muted);
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-md);

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }
`;

/* ---------- KPI card ---------- */

const KpiBox = styled(Card)<{ $clickable?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);
  padding: var(--space-lg);
  ${({ $clickable }: { $clickable?: boolean }) =>
    $clickable && 'cursor: pointer; &:hover { box-shadow: var(--shadow-md); }'}
`;

const KpiIcon = styled.div<{ $accent?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--border-radius-md);
  background: ${({ $accent }: { $accent?: boolean }) => ($accent ? 'var(--accent-100)' : 'var(--primary-50)')};
  color: ${({ $accent }: { $accent?: boolean }) => ($accent ? 'var(--accent-700)' : 'var(--primary-700)')};
  flex-shrink: 0;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const KpiLabel = styled.p`
  margin: 0 0 var(--space-xs);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
`;

const KpiValue = styled.p`
  margin: 0;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
`;

const KpiDetail = styled.p`
  margin: var(--space-xs) 0 0;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
`;

interface KpiProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  details?: React.ReactNode[];
  accent?: boolean;
  onClick?: () => void;
}

const Kpi: React.FC<KpiProps> = ({ icon, label, value, details, accent, onClick }) => (
  <KpiBox $clickable={!!onClick} onClick={onClick}>
    <KpiIcon $accent={accent}>{icon}</KpiIcon>
    <div>
      <KpiLabel>{label}</KpiLabel>
      <KpiValue>{value}</KpiValue>
      {details?.map((d, i) => (
        <KpiDetail key={i}>{d}</KpiDetail>
      ))}
    </div>
  </KpiBox>
);

/* ---------- azioni rapide e meteo ---------- */

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--space-sm);
`;

const ActionButton = styled(Button).attrs({ $variant: 'secondary' as const })`
  justify-content: flex-start;
  padding: var(--space-md);
  font-size: var(--font-size-sm);
`;

const WeatherGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--space-md);
`;

const WeatherLocation = styled(Card)`
  padding: var(--space-md);

  h4 {
    margin: 0 0 var(--space-sm);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
  }
`;

const WeatherNotice = styled.p`
  margin: var(--space-sm) 0 0;
  font-size: var(--font-size-xs);
  color: var(--text-muted);

  button {
    background: none;
    border: none;
    padding: 0;
    margin-left: var(--space-xs);
    color: var(--primary-700);
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
  }
`;

const DocRow = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-md);
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  background: none;
  border: none;
  border-bottom: 1px solid var(--gray-100);
  text-align: left;
  cursor: pointer;
  font-family: var(--font-family);
  transition: background-color var(--transition-fast);

  &:hover {
    background: var(--gray-50);
  }

  &:last-child {
    border-bottom: none;
  }

  svg.doc-icon {
    width: 1.125rem;
    height: 1.125rem;
    color: var(--gray-500);
    flex-shrink: 0;
  }

  svg.doc-chevron {
    width: 1rem;
    height: 1rem;
    color: var(--gray-400);
    margin-left: auto;
    flex-shrink: 0;
  }
`;

const DocTitle = styled.span`
  font-size: var(--font-size-sm);
  color: var(--text-primary);
`;

const DocMeta = styled.span`
  font-size: var(--font-size-xs);
  color: var(--text-muted);
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-2xl);
  text-align: center;
  color: var(--text-secondary);

  svg {
    width: 2.5rem;
    height: 2.5rem;
    color: var(--error);
  }

  h2 {
    margin: 0;
    color: var(--text-primary);
    font-size: var(--font-size-xl);
  }
`;

/* ---------- pagina ---------- */

const CASSA_ROLES: Ruolo[] = ['admin', 'manager', 'banconista'];

const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
  const { user } = useAuth();

  const formatInteger = useCallback((value: number | null | undefined) => {
    return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(value ?? 0);
  }, []);

  const formatKg = useCallback((value: number | null | undefined) => {
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }, []);

  const formatEuro = useCallback((value: number | null | undefined) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }, []);

  const formatEuro0 = useCallback((value: number | null | undefined) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value ?? 0);
  }, []);

  const [stats, setStats] = useState<ExtendedDashboardStats | null>(null);
  const [incasso, setIncasso] = useState<IncassoSummary | null>(null);
  const [mieiDocumenti, setMieiDocumenti] = useState<DocumentoPersonale[]>([]);
  const [puntiVendita, setPuntiVendita] = useState<PuntoVendita[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<Ruolo>('banconista');
  const [serverTime, setServerTime] = useState<Date | null>(null);

  // Ora del server, sincronizzata una volta e fatta avanzare in locale
  useEffect(() => {
    let interval: NodeJS.Timeout;
    const fetchTime = async () => {
      try {
        const health = await ApiService.healthCheck();
        const offset = new Date(health.timestamp).getTime() - Date.now();
        setServerTime(new Date(Date.now() + offset));
        interval = setInterval(() => {
          setServerTime(new Date(Date.now() + offset));
        }, 1000);
      } catch (e) {
        console.warn("Impossibile ottenere l'ora del server:", e);
      }
    };
    fetchTime();
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const canSeeCassa = CASSA_ROLES.includes((user?.ruolo as Ruolo) ?? 'produzione');

      const [dashboardData, adminStats, costoTotale, puntiVenditaData, incassoData, documentiData] = await Promise.all([
        ApiService.getDashboardStats(),
        user?.ruolo === 'admin' ? ApiService.getAdminStats() : Promise.resolve(null),
        ApiService.getCostoTotale(),
        ApiService.getPuntiVendita(),
        canSeeCassa ? ApiService.getIncassoSummary().catch(() => null) : Promise.resolve(null),
        ApiService.getMieiDocumenti().catch(() => []),
      ]);

      const extendedStats: ExtendedDashboardStats = {
        ...dashboardData,
        totale_produzioni: dashboardData.totaleProduzioni ?? adminStats?.produzioni ?? 0,
        punti_vendita: adminStats?.punti_vendita ?? puntiVenditaData.length,
        utenti_attivi: adminStats?.utenti ?? 0,
        costo_totale: costoTotale?.costo_totale || 0,
        produzioni_ultimi_7_giorni: dashboardData.produzioniUltimi7Giorni ?? 0,
      };

      setStats(extendedStats);
      setPuntiVendita(puntiVenditaData);
      setIncasso(incassoData?.success ? incassoData : null);
      setMieiDocumenti(Array.isArray(documentiData) ? documentiData : []);
    } catch (err: any) {
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [user?.ruolo]);

  useEffect(() => {
    if (user?.ruolo) {
      setViewMode(user.ruolo as Ruolo);
    }
  }, [user?.ruolo]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const anno = new Date().getFullYear();
  // Nel database esistono anche ruoli legacy (es. 'operatore', il vecchio
  // default della colonna): qualunque valore fuori dai quattro noti ricade
  // sulla vista operativa di produzione invece di mandare in errore la pagina.
  const RUOLI_NOTI: Ruolo[] = ['admin', 'manager', 'produzione', 'banconista'];
  const rawRole = user?.ruolo === 'admin' ? viewMode : user?.ruolo;
  const currentRole: Ruolo = RUOLI_NOTI.includes(rawRole as Ruolo) ? (rawRole as Ruolo) : 'produzione';

  const oraAggiornamentoCassa = incasso?.ultimo_aggiornamento
    ? new Date(incasso.ultimo_aggiornamento).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    : null;

  /* ---- sezioni ---- */

  const renderCassa = () => {
    if (!CASSA_ROLES.includes(currentRole)) return null;
    if (!incasso) {
      return (
        <>
          <SectionHeader>
            <Wallet />
            Cassa
          </SectionHeader>
          <Alert $variant="info">
            I dati di cassa non sono disponibili. Verifica l'import Kiara o apri{' '}
            <button
              onClick={() => onNavigate?.('incasso')}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-700)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
            >
              Incasso del Giorno
            </button>
            .
          </Alert>
        </>
      );
    }

    const meta = oraAggiornamentoCassa
      ? `aggiornato alle ${oraAggiornamentoCassa}`
      : 'dati di oggi non ancora importati';

    if (currentRole === 'banconista') {
      return (
        <>
          <SectionHeader>
            <Wallet />
            Cassa
            <span className="meta">{meta}</span>
          </SectionHeader>
          <KpiGrid>
            <Kpi
              accent
              icon={<Coins />}
              label="Contanti oggi"
              value={formatEuro(incasso.oggi.contanti)}
            />
          </KpiGrid>
        </>
      );
    }

    return (
      <>
        <SectionHeader>
          <Wallet />
          Cassa
          <span className="meta">{meta}</span>
        </SectionHeader>
        <KpiGrid>
          <Kpi
            accent
            icon={<Banknote />}
            label="Incasso oggi"
            value={formatEuro(incasso.oggi.totale)}
            details={[`ultimi 7 giorni: ${formatEuro(incasso.ultimi7.totale)}`]}
          />
          <Kpi
            accent
            icon={<Coins />}
            label="Contanti oggi"
            value={formatEuro(incasso.oggi.contanti)}
            details={[`mese: ${formatEuro(incasso.mese.contanti)}`]}
          />
          <Kpi
            accent
            icon={<CreditCard />}
            label="Elettronico oggi"
            value={formatEuro(incasso.oggi.elettronico)}
            details={[`mese: ${formatEuro(incasso.mese.elettronico)}`]}
          />
          {currentRole === 'manager' ? (
            <Kpi
              accent
              icon={<Landmark />}
              label="Contanti da preparare"
              value={formatEuro(incasso.da_preparare?.totale)}
              details={[
                incasso.da_preparare && incasso.da_preparare.settimane > 0
                  ? `${incasso.da_preparare.settimane} settimane da versare`
                  : 'nessuna settimana in sospeso',
              ]}
              onClick={() => onNavigate?.('versamento-cassa')}
            />
          ) : (
            <Kpi
              accent
              icon={<Wallet />}
              label="Incasso mese"
              value={formatEuro(incasso.mese.totale)}
              details={[`anno ${anno}: ${formatEuro(incasso.anno.totale)}`]}
            />
          )}
        </KpiGrid>
      </>
    );
  };

  const renderProduzione = () => (
    <>
      <SectionHeader>
        <Factory />
        Produzione
      </SectionHeader>
      <KpiGrid>
        <Kpi
          icon={<IceCream />}
          label="Kg prodotti"
          value={formatKg(stats?.produzioneAnno)}
          details={[`anno ${anno}`, `totale storico: ${formatKg(stats?.produzioneTotale)} kg`]}
        />
        <Kpi
          icon={<TrendingUp />}
          label="Gelato venduto"
          value={formatKg(stats?.vendutoAnno)}
          details={[
            `anno ${anno} — stima da produzione, giacenze e smaltimenti`,
            `totale storico: ${formatKg(stats?.vendutoTotale)} kg`,
          ]}
        />
        <Kpi
          icon={<Trash2 />}
          label="Smaltimento"
          value={formatKg(stats?.smaltimentoAnno)}
          details={[`anno ${anno}`, `totale storico: ${formatKg(stats?.smaltimentoTotale)} kg`]}
        />
        <Kpi
          icon={<ClipboardList />}
          label="Produzioni registrate"
          value={formatInteger(stats?.totale_produzioni)}
          details={[`${formatInteger(stats?.produzioni_ultimi_7_giorni)} negli ultimi 7 giorni`]}
        />
      </KpiGrid>
    </>
  );

  const renderSistema = () => (
    <>
      <SectionHeader>
        <BarChart3 />
        Costi e sistema
      </SectionHeader>
      <KpiGrid>
        <Kpi
          icon={<BarChart3 />}
          label="Costo ingredienti"
          value={formatEuro0(stats?.costo_totale)}
          details={[`anno ${anno}, calcolato dalle ricette`]}
        />
        <Kpi
          icon={<Users />}
          label="Utenti attivi"
          value={formatInteger(stats?.utenti_attivi)}
          details={['nel sistema']}
        />
        <Kpi
          icon={<MapPin />}
          label="Punti vendita"
          value={formatInteger(stats?.punti_vendita)}
        />
      </KpiGrid>
    </>
  );

  const renderOggi = () => (
    <>
      <SectionHeader>
        <Calendar />
        Situazione
      </SectionHeader>
      <KpiGrid>
        {currentRole === 'produzione' && (
          <Kpi
            icon={<IceCream />}
            label="Produzione oggi"
            value={`${formatKg(stats?.produzioneOggi)} kg`}
          />
        )}
        <Kpi
          icon={<Archive />}
          label="Giacenza totale"
          value={`${formatKg(stats?.giacenzaTotale)} kg`}
          details={['ultima rilevazione per punto vendita']}
        />
        <Kpi
          icon={<Trash2 />}
          label="Smaltimento anno"
          value={`${formatKg(stats?.smaltimentoAnno)} kg`}
        />
      </KpiGrid>
    </>
  );

  const quickActions: Record<Ruolo, { id: string; label: string; icon: React.ReactNode }[]> = {
    admin: [
      { id: 'produzione', label: 'Nuova Produzione', icon: <PlusCircle /> },
      { id: 'incasso', label: 'Incasso del Giorno', icon: <Banknote /> },
      { id: 'storico-produzione', label: 'Storico Produzione', icon: <FileText /> },
      { id: 'calendario-produzione', label: 'Calendario', icon: <Calendar /> },
      { id: 'analytics', label: 'Analytics', icon: <BarChart3 /> },
      { id: 'controllo-gestione', label: 'Controllo di gestione', icon: <TrendingUp /> },
    ],
    manager: [
      { id: 'produzione', label: 'Nuova Produzione', icon: <PlusCircle /> },
      { id: 'incasso', label: 'Incasso del Giorno', icon: <Banknote /> },
      { id: 'storico-produzione', label: 'Storico Produzione', icon: <FileText /> },
      { id: 'calendario-produzione', label: 'Calendario', icon: <Calendar /> },
      { id: 'controllo-gestione', label: 'Controllo di gestione', icon: <TrendingUp /> },
    ],
    produzione: [
      { id: 'produzione', label: 'Nuova Produzione', icon: <PlusCircle /> },
      { id: 'giacenza', label: 'Rileva Giacenza', icon: <Archive /> },
      { id: 'smaltimento', label: 'Registra Smaltimento', icon: <Trash2 /> },
      { id: 'storico-produzione', label: 'Storico Produzione', icon: <FileText /> },
    ],
    banconista: [
      { id: 'incasso', label: 'Incasso del Giorno', icon: <Banknote /> },
      { id: 'giacenza', label: 'Rileva Giacenza', icon: <Archive /> },
      { id: 'turni', label: 'Turni', icon: <Clock /> },
    ],
  };

  const renderMieiDocumenti = () => {
    if (mieiDocumenti.length === 0) return null;
    const nonLetti = mieiDocumenti.filter((d) => !d.visto_dal_dipendente_at).length;
    const recenti = mieiDocumenti.slice(0, 3);
    const meseNome = (m: number | null) =>
      m ? new Date(2000, m - 1, 1).toLocaleDateString('it-IT', { month: 'long' }) : null;

    return (
      <>
        <SectionHeader>
          <FileText />
          I miei documenti
          {nonLetti > 0 && <Badge $variant="accent">{nonLetti} da leggere</Badge>}
          <span className="meta">{mieiDocumenti.length} in archivio</span>
        </SectionHeader>
        <Card>
          {recenti.map((doc) => (
            <DocRow key={doc.id} onClick={() => onNavigate?.('personale')}>
              <FileText className="doc-icon" />
              <div>
                <DocTitle>{doc.titolo}</DocTitle>{' '}
                <DocMeta>
                  {[meseNome(doc.mese), doc.anno].filter(Boolean).join(' ')}
                </DocMeta>
                {!doc.visto_dal_dipendente_at && (
                  <>
                    {' '}
                    <Badge $variant="accent">nuovo</Badge>
                  </>
                )}
              </div>
              <ChevronRight className="doc-chevron" />
            </DocRow>
          ))}
          <DocRow onClick={() => onNavigate?.('personale')}>
            <DocTitle style={{ color: 'var(--primary-700)', fontWeight: 500 }}>
              Vedi tutti i documenti
            </DocTitle>
            <ChevronRight className="doc-chevron" />
          </DocRow>
        </Card>
      </>
    );
  };

  const renderAzioni = () => (
    <>
      <SectionHeader>
        <PlusCircle />
        Azioni rapide
      </SectionHeader>
      <ActionsGrid>
        {(quickActions[currentRole] ?? []).map((a) => (
          <ActionButton key={a.id} onClick={() => onNavigate?.(a.id)}>
            {a.icon}
            {a.label}
          </ActionButton>
        ))}
      </ActionsGrid>
    </>
  );

  const puntiConCoordinate = puntiVendita.filter((p) => p.latitudine && p.longitudine);
  const puntiSenzaCoordinate = puntiVendita.filter((p) => !p.latitudine || !p.longitudine);

  const renderMeteo = () => {
    if (puntiVendita.length === 0) return null;
    return (
      <>
        <SectionHeader>
          <Cloud />
          Meteo punti vendita
        </SectionHeader>
        <WeatherGrid>
          {puntiConCoordinate.slice(0, 3).map((punto) => (
            <WeatherLocation key={punto.id}>
              <h4>{punto.nome}</h4>
              <WeatherWidget puntoVenditaId={punto.id} compact={true} />
            </WeatherLocation>
          ))}
        </WeatherGrid>
        {puntiSenzaCoordinate.length > 0 && (
          <WeatherNotice>
            Alcuni punti vendita non hanno coordinate impostate.
            {currentRole === 'admin' && (
              <button onClick={() => onNavigate?.('punti-vendita')}>Configura</button>
            )}
          </WeatherNotice>
        )}
      </>
    );
  };

  /* ---- stati di pagina ---- */

  if (loading) {
    return (
      <PageContainerWide>
        <LoadingState label="Caricamento dashboard..." />
      </PageContainerWide>
    );
  }

  if (error) {
    return (
      <PageContainerWide>
        <ErrorWrap>
          <AlertTriangle />
          <h2>Errore nel caricamento</h2>
          <p>{error}</p>
          <Button onClick={loadDashboardData}>
            <RefreshCw />
            Riprova
          </Button>
        </ErrorWrap>
      </PageContainerWide>
    );
  }

  const oggiLabel = new Date().toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <PageContainerWide>
      <HeaderRow>
        <div>
          <HeaderTitle>Dashboard</HeaderTitle>
          <HeaderSubtitle>
            Bentornato, {user?.nome} — {oggiLabel}
          </HeaderSubtitle>
        </div>
        <HeaderAside>
          {user?.ruolo === 'admin' && (
            <ViewPicker>
              <label htmlFor="view-mode">Anteprima vista:</label>
              <Select
                id="view-mode"
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as Ruolo)}
              >
                <option value="admin">Amministratore</option>
                <option value="manager">Manager</option>
                <option value="produzione">Produzione</option>
                <option value="banconista">Banconista</option>
              </Select>
            </ViewPicker>
          )}
          <ServerClock>
            <Clock />
            Ora server: {serverTime ? serverTime.toLocaleTimeString('it-IT') : '...'}
          </ServerClock>
        </HeaderAside>
      </HeaderRow>

      {renderCassa()}

      {(currentRole === 'admin' || currentRole === 'manager') && renderProduzione()}
      {currentRole === 'admin' && renderSistema()}
      {(currentRole === 'produzione' || currentRole === 'banconista') && renderOggi()}

      {renderMieiDocumenti()}
      {renderAzioni()}
      {renderMeteo()}
    </PageContainerWide>
  );
};

export default DashboardPage;
